import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Spinner, Flex, Text } from "@radix-ui/themes";
import { getImportMeta, type ImportMeta } from "../../../../lib/import-db";
import { UploadPhase } from "./UploadPhase";
import { PlanningPhase } from "./PlanningPhase";

type ImportPhase = "loading" | "upload" | "planning";

// Re-export ImportMeta type with the expected interface name for compatibility
export type ImportRecord = ImportMeta;

export function Import() {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<ImportPhase>("loading");
  const [importRecord, setImportRecord] = useState<ImportRecord | null>(null);

  useEffect(() => {
    async function checkImportStatus() {
      try {
        const meta = await getImportMeta();

        if (meta && meta.uploadCompleted) {
          setImportRecord(meta);
          setPhase("planning");
        } else {
          setPhase("upload");
        }
      } catch (error) {
        console.error("Error checking import status:", error);
        setPhase("upload");
      }
    }

    checkImportStatus();
  }, []);

  const handleUploadComplete = (record: ImportRecord) => {
    setImportRecord(record);
    setPhase("planning");
  };

  const handleImportCleared = () => {
    setImportRecord(null);
    setPhase("upload");
  };

  if (phase === "loading") {
    return (
      <Box p="6">
        <Flex align="center" justify="center" style={{ minHeight: "300px" }}>
          <Spinner size="3" />
          <Text ml="3" color="gray">
            {t("import.loadingStatus")}
          </Text>
        </Flex>
      </Box>
    );
  }

  if (phase === "upload") {
    return <UploadPhase onComplete={handleUploadComplete} />;
  }

  return (
    <PlanningPhase
      importRecord={importRecord!}
      onCleared={handleImportCleared}
    />
  );
}
